import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  FormLabel,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { chipHtml, fromEditorHtml, MergeFieldInfo, toEditorHtml } from '../../utils/emailEditor';

interface Props {
  label: string;
  value: string; // stored form: sanitised HTML with {{merge_field}} tokens
  fields: MergeFieldInfo[];
  onChange: (stored: string) => void;
  onRemove?: () => void;
  testId?: string;
}

/**
 * Minimal rich text: bold, italic, link, merge-field chips. Nothing else can be produced:
 * the output goes through fromEditorHtml, which keeps only b, i, a[https|mailto], br, p.
 */
const RichTextField: React.FC<Props> = ({ label, value, fields, onChange, onRemove, testId }) => {
  const ref = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const lastEmitted = useRef<string>(value);
  const [linkUrl, setLinkUrl] = useState('https://');
  const linkPopover = useDisclosure();

  // Load the stored value into the editor; only when it changed from outside (reset, switch email),
  // never while the user is typing (that would move the caret).
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastEmitted.current || ref.current.innerHTML === '') {
      ref.current.innerHTML = toEditorHtml(value, fields);
      lastEmitted.current = value;
    }
  }, [value, fields]);

  const emit = () => {
    if (!ref.current) return;
    const stored = fromEditorHtml(ref.current.innerHTML);
    lastEmitted.current = stored;
    onChange(stored);
  };

  const rememberSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const command = (name: 'bold' | 'italic') => {
    restoreSelection();
    document.execCommand(name);
    emit();
  };

  const applyLink = () => {
    if (!/^(https:\/\/|mailto:)/i.test(linkUrl) || linkUrl.length < 9) return;
    restoreSelection();
    document.execCommand('createLink', false, linkUrl);
    linkPopover.onClose();
    emit();
  };

  const insertField = (f: MergeFieldInfo) => {
    restoreSelection();
    const sel = window.getSelection();
    const editor = ref.current;
    if (!sel || !editor) return;
    let range = sel.rangeCount ? sel.getRangeAt(0) : null;
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      // No caret in the editor yet: append at the end of the last paragraph.
      range = document.createRange();
      range.selectNodeContents(editor.lastElementChild || editor);
      range.collapse(false);
    }
    // Insert the chip node itself (not via execCommand, which opens a new paragraph in Chrome).
    const holder = document.createElement('span');
    holder.innerHTML = chipHtml(f.key, f.label);
    const chip = holder.firstChild as Node;
    const space = document.createTextNode('\u00a0');
    range.deleteContents();
    range.insertNode(space);
    range.insertNode(chip);
    range.setStartAfter(space);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    emit();
  };

  return (
    <Box data-testid={testId}>
      <HStack justify="space-between" mb={1}>
        <FormLabel m={0} fontSize="sm">{label}</FormLabel>
        {onRemove && (
          <Button size="xs" variant="ghost" colorScheme="red" onClick={onRemove}>
            Supprimer ce texte
          </Button>
        )}
      </HStack>
      <HStack mb={1} spacing={2} wrap="wrap">
        <ButtonGroup size="xs" isAttached variant="outline">
          <Button aria-label="Gras" fontWeight="bold" onMouseDown={(e) => e.preventDefault()} onClick={() => command('bold')}>G</Button>
          <Button aria-label="Italique" fontStyle="italic" onMouseDown={(e) => e.preventDefault()} onClick={() => command('italic')}>I</Button>
        </ButtonGroup>
        <Popover isOpen={linkPopover.isOpen} onClose={linkPopover.onClose} onOpen={() => { rememberSelection(); linkPopover.onOpen(); }}>
          <PopoverTrigger>
            <Button size="xs" variant="outline" aria-label="Lien" onMouseDown={rememberSelection}>Lien</Button>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody>
              <HStack>
                <Input size="sm" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" />
                <Button size="sm" colorScheme="blue" onClick={applyLink}>OK</Button>
              </HStack>
              <Box fontSize="xs" color="gray.500" mt={1}>Sélectionnez d'abord le texte à lier. Adresses https:// ou mailto: uniquement.</Box>
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <Menu>
          <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} onMouseDown={rememberSelection}>
            Insérer un champ
          </MenuButton>
          <MenuList>
            {fields.map((f) => (
              <MenuItem key={f.key} fontSize="sm" onClick={() => insertField(f)}>{f.label}</MenuItem>
            ))}
          </MenuList>
        </Menu>
      </HStack>
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        onInput={emit}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        onBlur={() => { rememberSelection(); emit(); }}
        minH="90px"
        p={3}
        borderWidth="1px"
        borderRadius="md"
        bg="white"
        fontSize="sm"
        lineHeight="1.6"
        _focus={{ outline: 'none', borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299e1' }}
        sx={{ p: { margin: 0, marginBottom: '0.5em' }, a: { color: '#3182ce', textDecoration: 'underline' } }}
      />
    </Box>
  );
};

export default RichTextField;
